import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { PlayerProvider } from '@context/PlayerContext';
import { ToastContainer, Slide } from 'react-toastify';
import {
  useParams,
  Outlet,
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import { Layout, message } from 'antd';
import { useDispatch } from 'react-redux';
import {
  setAssetVariant,
  removeComment,
  editComment,
  setAsset,
  setCommentLike,
  setCommentResolve
} from '@redux/actions/mediaViewerAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import MediaViewerHeader from '@components/MediaViewerHeader';
import WorkspacesPanel from '@components/WorkspacesPanel';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import {
  CreateAssetVersionCommentDto,
  GetAssetVersionCommentsSuccessDto,
  ReplyAssetVersionCommentDto,
  GetAssetVersionTasksLastReadSuccessDto
} from '@api/Api';
import {
  assetInternalCommentsCreateAssetComment,
  assetInternalCommentsDeleteAssetComment,
  assetInternalCommentsGetAssetComments,
  assetInternalCommentsListCommentPotentialMentions,
  assetInternalCommentsMarkAssetCommentsRead,
  assetInternalCommentsReplyAssetComment,
  assetInternalCommentsSetAssetCommentLike,
  assetInternalCommentsSetAssetCommentResolve,
  assetInternalCommentsUpdateAssetComment,
  assetSetRecentlyOpened
} from '@api/Asset';
import { useTypedSelector } from '@hooks';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import { MixpanelService, MixpanelEventType } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import { ReactComponent as ErrorSvg } from '@assets/icons/error.svg';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { apiClient } from '@api/client';
import type { MediaViewerHeaderProps } from '@components/MediaViewerHeader/MediaViewerHeader';
import { useAssetQuery } from '@hooks/queries/assets';
import {
  QUERY_COMMENTS_ASSETS,
  useInternalCommentsAssetReviewQuery
} from '@hooks/queries/asset';
import {
  QUERY_TASKS_LAST_READ,
  useAssetVersionTasksLastReadQuery
} from '@hooks/queries/task';
import { taskMarkAssetVersionTasksRead } from '@api/Task';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';
import DemoModePanel, {
  DEMO_MODE_PANEL_HEIGHT
} from '@components/DemoModePanel/DemoModePanel';
import { useLayoutEffectWithPrev } from '@hooks/useEffectWithPrev';
import { showTranscriptionReadyToast } from '@helpers/showTranscriptionReadyToast';
import { showNewAssetVersionToast } from '@helpers/showNewAssetVersionToast';
import { nonNull } from '@helpers/non-null';
import { useQueryClient } from '@tanstack/react-query';

const { Header, Content, Sider } = Layout;

type MediaViewerLayoutProps = {
  headerProps?: MediaViewerHeaderProps;
};

function MediaViewerLayout({ headerProps = {} }: MediaViewerLayoutProps) {
  const { assetId, versionId: versionIdParam } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDemoOrganization = useIsDemoOrganization();
  const [searchParams] = useSearchParams();
  const { user } = useAuth(false, 'internal');
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspacesListSiderWidth = 65;
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const queryClient = useQueryClient();
  const assetVersionId = selectedVersion?.id;
  const prevVersionId = useRef(assetVersionId || null);
  const containerHeight = isDemoOrganization
    ? `calc(100vh - ${DEMO_MODE_PANEL_HEIGHT})`
    : '100vh';

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useInternalCommentsAssetReviewQuery(
      {
        assetVersionId: assetVersionId || ''
      },
      {
        keepPreviousData: assetVersionId === prevVersionId.current,
        enabled: !!assetVersionId
      }
    );
  const commentsLastReadAt = commentsList?.lastReadAt ?? null;
  const commentsItems = commentsList?.comments ?? [];

  const { data: tasksLastReadData } = useAssetVersionTasksLastReadQuery(
    { assetVersionId: assetVersionId || '' },
    { enabled: !!assetVersionId }
  );
  const tasksLastReadAt = tasksLastReadData?.lastReadAt ?? null;
  const latestTaskCreatedAt = tasksLastReadData?.latestCreatedAt ?? null;

  if (assetVersionId !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = assetVersionId || null;
  }

  useEffect(() => {
    if (!assetId || !assetVersionId) return;
    (async () => {
      await assetSetRecentlyOpened({ assetId, assetVersionId });
    })();
  }, [assetId, assetVersionId]);

  const outletContext = useMemo<MediaViewerOutletContextState>(
    () => ({
      fetchCommentsList: async ({ assetVersionId }) => {
        const { data } = await assetInternalCommentsGetAssetComments({
          assetVersionId
        });
        return data.comments;
      },
      addNewComment: async (data, parentId) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await assetInternalCommentsReplyAssetComment(
              data as ReplyAssetVersionCommentDto
            );
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await assetInternalCommentsCreateAssetComment(
              data as CreateAssetVersionCommentDto
            );
            newComment = comment;
            if (user) {
              MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
                addToFavorite: false,
                deleteAsset: false,
                newAsset: false,
                externalLinkShare: false,
                approvalRouting: false,
                newCommentAsset: true,
                annotationLeft: !!comment.annotations,
                taskFromComment: false
              });
            }
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id) => {
        try {
          await removeComment(id);
          await assetInternalCommentsDeleteAssetComment({ id });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data) => {
        try {
          const {
            data: { comment }
          } = await assetInternalCommentsUpdateAssetComment(data);
          editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data, user) => {
        try {
          await setCommentLike(data, user);
          await assetInternalCommentsSetAssetCommentLike(data);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentResolve: async (data, user) => {
        await setCommentResolve(data, user);
        await assetInternalCommentsSetAssetCommentResolve(data);
      },
      getPotentialMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } = await assetInternalCommentsListCommentPotentialMentions({
          assetVersionId,
          searchQuery
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({
        assetVersionId,
        searchQuery
      }: {
        assetVersionId: string;
        searchQuery?: string;
      }) => {
        const {
          data: { edges }
        } =
          await apiClient.asset.assetInternalCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsItems,
      commentsLastReadAt,
      markCommentsRead: async () => {
        if (!assetVersionId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await assetInternalCommentsMarkAssetCommentsRead({
          assetVersionId
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      tasksLastReadAt,
      latestTaskCreatedAt,
      markTasksRead: async () => {
        if (!assetVersionId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, assetVersionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await taskMarkAssetVersionTasksRead({
          assetVersionId
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, assetVersionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      workspace: currentWorkspace
    }),
    [
      currentWorkspace,
      isLoadingCommentsList,
      user,
      commentsItems,
      commentsLastReadAt,
      tasksLastReadAt,
      latestTaskCreatedAt,
      assetVersionId,
      queryClient
    ]
  );

  const assetQuery = useAssetQuery(
    { assetId: assetId ?? '' },
    { enabled: !!assetId }
  );

  const asset = assetQuery.data ?? null;

  useLayoutEffect(() => {
    dispatch(toggleApprovalModal(null));
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetId, dispatch]);

  // useLayoutEffect(() => {
  //   if (!selectedVersion?.id) return;
  //   dispatch({
  //     type: MediaViewerActionTypes.SET_RESET_ASSET_VERSION_STATE
  //   });
  // }, [dispatch, versionIdParam]);

  useLayoutEffectWithPrev(
    ([prevAsset, prevVersionId]) => {
      dispatch(setAsset(asset));
      if (asset?.workspaceId) {
        setCurrentWorkspace(asset.workspaceId);
      }
      const version =
        asset?.asset?.versions.find((v) => v.id === versionIdParam) ??
        asset?.asset?.versions[0];
      dispatch(setAssetVariant(version?.id ?? null));

      let newVersionAdded = false;
      let newVersionAddedByMe = false;
      if (prevAsset?.id === asset?.id) {
        const optimisticUpdate =
          !!(prevAsset as any)?.versionsOptimisticUpdate ||
          !!(asset as any)?.versionsOptimisticUpdate;
        const prevVersionIds = (prevAsset?.asset?.versions ?? []).map(
          (v) => v.id
        );
        const newVersionIds = (asset?.asset?.versions ?? []).map((v) => v.id);
        newVersionAdded =
          !optimisticUpdate &&
          newVersionIds[0] !== prevVersionId &&
          newVersionIds.some((id) => !prevVersionIds.includes(id));
        if (newVersionAdded) {
          const newVersion = asset?.asset?.versions.find(
            (v) => !prevVersionIds.includes(v.id)
          );
          newVersionAddedByMe = !!newVersion?.user?.me;
        }
      }

      if (asset && version?.id !== versionIdParam) {
        const version = asset?.asset?.versions[0];
        navigate(`/media-viewer/${assetId}/${version?.id}`, {
          replace: true
        });
      } else if (newVersionAdded) {
        const goToLatestVersion = () => navigate(`/media-viewer/${assetId}`);
        if (newVersionAddedByMe) goToLatestVersion();
        else showNewAssetVersionToast(nonNull(assetId), goToLatestVersion);
      }
    },
    [asset, versionIdParam, dispatch, setCurrentWorkspace]
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== assetVersionId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetId) return;
    if (msg.payload.assetVersionId !== assetVersionId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== assetId) return;
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
      });
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== assetId) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== assetId) return;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, assetVersionId]
      });
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.assetVersionId !== assetVersionId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async (messages) => {
      const msg = messages[messages.length - 1];
      const payload = msg?.payload;
      const isCurrentVersion = payload?.assetVersionId === assetVersionId;
      const isTranscriptionCompleted =
        payload?.reason === 'transcription-completed';
      if (isTranscriptionCompleted) {
        showTranscriptionReadyToast({
          payload,
          showViewButton: !isCurrentVersion,
          onViewTranscript: () => {
            navigate(
              `/media-viewer/${payload?.assetId}/${payload?.assetVersionId}`
            );
          }
        });
      }
      assetQuery.refetch();
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  return (
    <>
      {isDemoOrganization && <DemoModePanel />}
      <Layout
        style={{
          minHeight: containerHeight,
          background: '#1A1C1E',
          color: '#fff',
          ...(isDemoOrganization
            ? ({
                '--demo-mode-panel-width': DEMO_MODE_PANEL_HEIGHT
              } as React.CSSProperties)
            : {})
        }}
      >
        <Sider
          style={{
            overflow: 'auto',
            height: containerHeight,
            position: 'fixed',
            left: 0
          }}
          className="workspaces_sider b-bg-dark"
          width={workspacesListSiderWidth}
        >
          <WorkspacesPanel />
        </Sider>
        <Layout
          className="site-layout"
          style={{
            background: '#1A1C1E',
            marginLeft: workspacesListSiderWidth
          }}
        >
          {!assetQuery.isError && (
            <Header
              className="site-layout-background b-background-black"
              style={{
                width: `calc(100% - ${workspacesListSiderWidth}px)`,
                padding: 0
              }}
            >
              <MediaViewerHeader {...headerProps} />
            </Header>
          )}

          <Content style={{ marginTop: 70 }}>
            <div style={{ minHeight: 360, height: '100%' }}>
              {assetQuery.isError ? (
                <div className="media_viewer_content_deleted">
                  <ErrorSvg />
                  <h3>Oops…looks like that media was deleted.</h3>
                </div>
              ) : (
                <PlayerProvider>
                  <Outlet context={outletContext} />
                </PlayerProvider>
              )}
            </div>
          </Content>
        </Layout>
        <ToastContainer
          transition={Slide}
          closeOnClick={false}
          closeButton={false}
          position="top-center"
          theme="dark"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          progressClassName="fancy-progress-bar"
        />
      </Layout>
    </>
  );
}

export default MediaViewerLayout;
